import React, { useEffect, useState } from 'react';
import {
    Avatar,
    TextField,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Pagination,
    Typography,
    IconButton,
    Dialog,
    DialogTitle,
    DialogActions,
    Button,
    Chip, // --- ADDED IMPORT ---
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import axios from 'axios';
import DeleteIcon from '@mui/icons-material/Delete';

const SearchBar = styled(TextField)({
    marginBottom: '20px',
    width: '100%',
    backgroundColor: '#202020',
    borderRadius: '8px',
    color: '#fff',
    '& .MuiInputBase-root': {
        color: '#fff',
        fontSize: '1rem',
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#12c099',
    },
    '& .MuiInputLabel-root': {
        color: '#12c099',
        fontSize: '1rem',
    },
});

const StyledTableContainer = styled(TableContainer)({
    backgroundColor: '#202020',
    borderRadius: '12px',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
    overflowX: 'auto',
    '@media (max-width: 768px)': {
        width: '100%',
    },
});

const CompactTableCell = styled(TableCell)({
    padding: '10px 16px',
    fontSize: '0.9rem',
    '@media (max-width: 768px)': {
        display: 'block',
        width: '100%',
        padding: '12px 0',
        fontSize: '0.9rem',
        textAlign: 'left',
    },
    color: '#fff',
    whiteSpace: 'nowrap',
});

// --- UPDATED ROW TO HIGHLIGHT BASED ON PROP ---
const CompactTableRow = styled(TableRow)(({ 'data-deletion-requested': deletionRequested }) => ({
    backgroundColor: deletionRequested ? 'rgba(255, 23, 68, 0.1)' : 'transparent',
    '&:hover': {
        backgroundColor: deletionRequested ? 'rgba(255, 23, 68, 0.2)' : 'rgba(255, 255, 255, 0.05)',
    },
    '@media (max-width: 768px)': {
        display: 'block',
        width: '100%',
        marginBottom: '20px',
        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
        borderRadius: '8px',
        backgroundColor: deletionRequested ? 'rgba(255, 23, 68, 0.1)' : '#202020',
        overflow: 'hidden',
    },
}));

const PaginationBox = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    marginTop: '20px',
    '@media (max-width: 768px)': {
        marginTop: '10px',
    },
});

const AthletesContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
});

const HeaderContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    flexWrap: 'wrap',
    '@media (max-width: 768px)': {
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
});

const Title = styled(Typography)({
    fontSize: '1.8rem',
    color: '#fff',
    fontWeight: 'bold',
    '@media (max-width: 768px)': {
        fontSize: '1.5rem',
    },
});

const Athletes = () => {
    const [athletes, setAthletes] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const rowsPerPage = 25;

    // --- UPDATED INITIAL SORT ---
    // Default sort by deletionRequested (desc) to match backend
    const [order, setOrder] = useState('desc');
    const [orderBy, setOrderBy] = useState('deletionRequested');
    // ---

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [selectedFirebaseUid, setSelectedFirebaseUid] = useState(null);
    const [geocodedData, setGeocodedData] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAthletes = async () => {
            try {
                // This fetches data pre-sorted by deletionRequested and dateJoined
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users`);
                setAthletes(response.data);
                fetchAndCacheGeocodedData(response.data);
            } catch (error) {
                console.error('Error fetching athletes data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchAthletes();
    }, []);

    const fetchAndCacheGeocodedData = async (allAthletes) => {
        const data = {};
        for (const athlete of allAthletes) {
            const uniqueId = athlete.firebaseUid;
            try {
                if (athlete.address?.city && athlete.address?.state) {
                    data[uniqueId] = { city: athlete.address.city, state: athlete.address.state, country: athlete.address.country };
                    continue;
                }
                const zipCode = athlete.address?.zipCode;
                if (!zipCode) {
                    data[uniqueId] = { city: 'N/A', state: 'N/A', country: 'N/A' };
                    continue;
                }
                const geoResponse = await axios.get(`https://maps.googleapis.com/maps/api/geocode/json?address=${zipCode}&key=${process.env.REACT_APP_GOOGLE_API_KEY}`);
                let locationData = { city: 'N/A', state: 'N/A', country: 'N/A' };
                if (geoResponse.data.results.length > 0) {
                    const addressComponents = geoResponse.data.results[0].address_components;
                    const formattedCity = addressComponents.find((comp) => comp.types.includes('locality'))?.long_name || addressComponents.find((comp) => comp.types.includes('sublocality'))?.long_name || 'N/A';
                    const formattedState = addressComponents.find((comp) => comp.types.includes('administrative_area_level_1'))?.short_name || 'N/A';
                    const formattedCountry = addressComponents.find((comp) => comp.types.includes('country'))?.long_name || 'N/A';
                    locationData = { city: formattedCity, state: formattedState, country: formattedCountry === 'United States' ? 'USA' : formattedCountry };
                }
                data[uniqueId] = locationData;
                await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/athlete/${uniqueId}`, { address: locationData });
            } catch (error) {
                console.error(`Geocoding/patching failed for athlete ${uniqueId}:`, error);
                data[uniqueId] = { city: 'N/A', state: 'N/A', country: 'N/A' };
            }
        }
        setGeocodedData((prevData) => ({ ...prevData, ...data }));
    };

    const handleSortRequest = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const openDeleteDialogHandler = (firebaseUid) => {
        setSelectedFirebaseUid(firebaseUid);
        setOpenDeleteDialog(true);
    };

    const closeDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedFirebaseUid(null);
    };

    const confirmDelete = async () => {
        try {
            // This is the old delete route, for the simple trash icon
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/delete/${selectedFirebaseUid}`);
            setAthletes(athletes.filter((athlete) => athlete.firebaseUid !== selectedFirebaseUid));
            closeDeleteDialog();
        } catch (error) {
            console.error('Error deleting athlete:', error);
        }
    };

    const filteredAthletes = athletes.filter((athlete) =>
        `${athlete.firstName || ''} ${athlete.lastName || ''}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // --- UPDATED SORTING LOGIC ---
    const sortedAthletes = filteredAthletes.sort((a, b) => {
        // 1. Primary Sort: deletionRequested (true comes first)
        if (a.deletionRequested && !b.deletionRequested) return -1;
        if (!a.deletionRequested && b.deletionRequested) return 1;

        // 2. Secondary Sort: Based on user's column choice
        let compare = 0;
        if (orderBy === 'dateJoined') {
            const dateA = a.dateJoined ? new Date(a.dateJoined) : 0;
            const dateB = b.dateJoined ? new Date(b.dateJoined) : 0;
            compare = dateA - dateB;
        } else {
            // This will handle
            compare = (a[orderBy] || '').toString().localeCompare((b[orderBy] || '').toString());
        }

        return order === 'asc' ? compare : -compare;
    });
    // --- END OF UPDATED SORTING ---

    const paginatedAthletes = sortedAthletes.slice((page - 1) * rowsPerPage, page * rowsPerPage);

    return (
        <AthletesContainer>
            <HeaderContainer>
                <Title variant="h4" gutterBottom>Users</Title>
                <SearchBar variant="outlined" label="Search Users" onChange={(e) => setSearchTerm(e.target.value)} />
            </HeaderContainer>
            <StyledTableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <CompactTableCell>
                                <TableSortLabel active={orderBy === 'firstName'} direction={orderBy === 'firstName' ? order : 'asc'} onClick={() => handleSortRequest('firstName')} style={{ color: '#12c099' }}>Name</TableSortLabel>
                            </CompactTableCell>
                            {/* --- ADDED STATUS COLUMN --- */}
                            <CompactTableCell>
                                <TableSortLabel active={orderBy === 'deletionRequested'} direction={orderBy ==='deletionRequested' ? order : 'asc'} onClick={() => handleSortRequest('deletionRequested')} style={{ color: '#12c099' }}>Status</TableSortLabel>
                            </CompactTableCell>
                            <CompactTableCell>
                                Plan
                            </CompactTableCell>
                            <CompactTableCell>
                                <TableSortLabel active={orderBy === 'dateJoined'} direction={orderBy === 'dateJoined' ? order : 'asc'} onClick={() => handleSortRequest('dateJoined')} style={{ color: '#12c099' }}>Date Joined</TableSortLabel>
                            </CompactTableCell>
                            <CompactTableCell>
                                <TableSortLabel active={orderBy === 'gender'} direction={orderBy === 'gender' ? order : 'asc'} onClick={() => handleSortRequest('gender')} style={{ color: '#12c099' }}>Gender</TableSortLabel>
                            </CompactTableCell>
                            <CompactTableCell>Location</CompactTableCell>
                            <CompactTableCell>Country</CompactTableCell>
                            <CompactTableCell>Actions</CompactTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedAthletes.map((athlete) => (
                            <CompactTableRow
                                key={athlete.firebaseUid}
                                // --- PASS DELETION STATUS TO STYLED COMPONENT ---
                                data-deletion-requested={athlete.deletionRequested}
                            >
                                <CompactTableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Avatar
                                            sx={{ marginRight: '10px', width: 28, height: 28, fontSize: '0.85rem' }}
                                            src={athlete.avatar}
                                        >
                                            {athlete.firstName?.charAt(0)}
                                        </Avatar>
                                        <Link
                                            to={`/athlete/${athlete.firebaseUid}`}
                                            style={{ color: '#12c099', textDecoration: 'none', fontSize: '0.9rem' }}
                                        >
                                            {`${athlete.firstName} ${athlete.lastName}`}
                                        </Link>
                                    </Box>
                                </CompactTableCell>
                                {/* --- RENDER STATUS BADGE --- */}
                                <CompactTableCell>
                                    {athlete.deletionRequested && (
                                        <Chip
                                            label="Deletion Requested"
                                            color="error"
                                            size="small"
                                            sx={{ backgroundColor: '#ff1744', color: 'white', fontWeight: 600 }}
                                        />
                                    )}
                                </CompactTableCell>
                                <CompactTableCell>
                                    {athlete.planDetails?.[0]?.isRecurring ? 'Recurring' : 'PayGo'}
                                </CompactTableCell>
                                <CompactTableCell>{athlete.dateJoined ? new Date(athlete.dateJoined).toLocaleDateString() : 'N/A'}</CompactTableCell>
                                <CompactTableCell>{athlete.gender || 'N/A'}</CompactTableCell>
                                <CompactTableCell>
                                    {geocodedData[athlete.firebaseUid] ? `${geocodedData[athlete.firebaseUid].city}, ${geocodedData[athlete.firebaseUid].state}` : 'Processing...'}
                                </CompactTableCell>
                                <CompactTableCell>
                                    {geocodedData[athlete.firebaseUid]?.country || '...'}
                                </CompactTableCell>
                                <CompactTableCell>
                                    <IconButton onClick={() => openDeleteDialogHandler(athlete.firebaseUid)} sx={{ color: '#ff1744' }}><DeleteIcon /></IconButton>
                                </CompactTableCell>
                            </CompactTableRow>
                        ))}
                    </TableBody>
                </Table>
            </StyledTableContainer>
            <PaginationBox>
                <Pagination count={Math.ceil(filteredAthletes.length / rowsPerPage)} page={page} onChange={(event, value) => setPage(value)} sx={{ button: { color: '#12c099' }, ul: { color: '#12c099' } }} />
            </PaginationBox>
            <Dialog open={openDeleteDialog} onClose={closeDeleteDialog} aria-labelledby="delete-dialog-title">
                <DialogTitle id="delete-dialog-title">Are you sure you want to delete this athlete?</DialogTitle>
                <DialogActions>
                    <Button onClick={closeDeleteDialog} color="primary">Cancel</Button>
                    <Button onClick={confirmDelete} color="secondary">Delete</Button>
                </DialogActions>
            </Dialog>
        </AthletesContainer>
    );
};

export default Athletes;
